import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {deleteAnalysisSteps} from 'analysis-tool/AnalysisActions';
import {ANALYSIS_TOOL_SPACING} from 'analysis-tool/AnalysisToolConstants';
import getDownstreamSteps from 'analysis-tool/data/getDownstreamSteps';
import {AnalysisRightSidebarItemType} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import RetryFailedSaveToResultsStepBanner from 'analysis-tool/structured-data-mapping/RetryFailedSaveToResultsStepBanner';
import useLogAnalysisProfilerTimingFinished from 'analysis-tool/ui/hooks/useLogAnalysisProfilerTimingFinished';
import {HaltedStepContent} from 'analysis-tool/ui/main-view/HaltedStepView';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import RunningStepView from 'analysis-tool/ui/main-view/RunningStepView';
import {assert} from 'Asserts';
import {PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SAVE_TO_RESULTS_FINISHED_EVENT} from 'performance/processAnalysesEvents';
import {DeletePipelineStepsBehavior} from 'pipelines/network/PipelineDAO';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

import DeleteFailedSaveToResultsStepBanner from './DeleteFailedSaveToResultsStepBanner';
import SucceededDataMappingResults from './SucceededDataMappingResults';

export default function SaveToResultsStepOutputView(
  props: MainViewComponentProps<AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT>
): JSX.Element {
  const {analysis, step, analysisClientStateId, clientStateId, onChangeSelectedRightSidebarItem} = props;
  useLogAnalysisProfilerTimingFinished(
    step.statusCode === PipelineStepStatusCode.WAITING || step.statusCode === PipelineStepStatusCode.RUNNING,
    [SAVE_TO_RESULTS_FINISHED_EVENT]
  );

  const openReconfigurationView = (): void => {
    onChangeSelectedRightSidebarItem({type: AnalysisRightSidebarItemType.EDIT_STEP, stepId: step.id});
  };

  function getContents(): ReactNode {
    switch (step.statusCode) {
      case PipelineStepStatusCode.FAILED:
        assert(step.statusMessage != null, 'Expected statusMessage to be defined for FAILED step.');
        if (getDownstreamSteps(analysis, step.id).size > 0) {
          return (
            <DeleteFailedSaveToResultsStepBanner
              deleteStep={
                analysis.permissions.writable
                  ? () =>
                      deleteAnalysisSteps(
                        analysis,
                        {
                          stepIds: [step.id],
                          behavior: DeletePipelineStepsBehavior.DELETE_ALL_CHILDREN,
                        },
                        analysisClientStateId
                      )
                  : undefined
              }
              supportingInfo={step.statusMessage}
            />
          );
        } else {
          return (
            <RetryFailedSaveToResultsStepBanner
              openReconfigurationView={analysis.permissions.writable ? openReconfigurationView : undefined}
              supportingInfo={step.statusMessage}
            />
          );
        }
      case PipelineStepStatusCode.SUCCEEDED:
      case PipelineStepStatusCode.SUCCEEDED_WITH_ERRORS:
        return (
          <SucceededDataMappingResults
            analysis={analysis}
            step={step}
            clientStateId={clientStateId}
            openReconfigurationView={openReconfigurationView}
          />
        );
      case PipelineStepStatusCode.RUNNING:
      case PipelineStepStatusCode.AI_PROPOSED:
      case PipelineStepStatusCode.BLOCKED:
      case PipelineStepStatusCode.WAITING:
        return (
          <RunningStepView
            {...props}
            selectedItem={{type: AnalysisMainViewItemType.RUNNING, stepId: step.id}}
          />
        );
      case PipelineStepStatusCode.HALTED:
        if (analysis.__typename === 'AnalysisTemplateNode') {
          return (
            <div
              className={css(styles.haltedWrapper)}
              {...getTestProps({component: 'SaveToResultsStepOutputView', element: 'halted'})}
            >
              <HaltedStepContent
                statusMessage={step.statusMessage}
                action={
                  analysis.permissions.writable ? (
                    <Button
                      buttonStyle={ButtonStyle.SECONDARY}
                      size={ButtonSize.MEDIUM}
                      onClick={openReconfigurationView}
                      text="Reconfigure"
                    />
                  ) : undefined
                }
              />
            </div>
          );
        }
        return null;
      default:
        throw new Error(`Unexpected step status for SAVE_TO_RESULTS: ${step.statusCode}`);
    }
  }

  return (
    <div
      className={classNames(
        css(styles.container),
        step.statusCode === PipelineStepStatusCode.RUNNING && css(styles.runningContainer)
      )}
      {...getTestProps({component: 'SaveToResultsStepOutputView', element: 'root'})}
    >
      {getContents()}
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    margin: ANALYSIS_TOOL_SPACING,
  },
  runningContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
  haltedWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    gap: 12,
  },
});
